import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Files, Lock, X } from 'lucide-react';
import { Checkbox } from './FieldComponents';
import '../styles/SplitArchiveChoiceModal.css';

const formatPartNumber = (partNumber) => String(partNumber).padStart(3, '0');

const flattenGroupFiles = (groups) => groups.flatMap((group) => group.files || []);

const getMissingFileNames = (group) => (
  (group.missingPartNumbers || []).map(
    (partNumber) => `${group.archiveName}.${formatPartNumber(partNumber)}`,
  )
);

/**
 * Lets a user choose whether a detected numbered ZIP delivery should be kept
 * as received or prepared into reviewable documents. Preparation callbacks
 * explicitly lock the original file names because numbered parts cannot be
 * safely renamed before the server joins them.
 */
const SplitArchiveChoiceModal = ({
  busy = false,
  detection,
  error = '',
  incompleteGroups: incompleteGroupsProp,
  onCancel,
  onKeepArchive,
  onPrepare,
  requestPassword = true,
  validGroups: validGroupsProp,
}) => {
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const busyRef = useRef(busy);
  const onCancelRef = useRef(onCancel);
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);

  const validGroups = useMemo(
    () => validGroupsProp || detection?.validGroups || [],
    [detection?.validGroups, validGroupsProp],
  );
  const incompleteGroups = useMemo(
    () => incompleteGroupsProp || detection?.incompleteGroups || [],
    [detection?.incompleteGroups, incompleteGroupsProp],
  );
  const allGroups = useMemo(
    () => [...validGroups, ...incompleteGroups],
    [validGroups, incompleteGroups],
  );
  const archiveFiles = useMemo(() => flattenGroupFiles(allGroups), [allGroups]);
  const unrelatedFileCount = detection?.unrelatedFiles?.length || 0;
  const canPrepare = validGroups.length === 1 && incompleteGroups.length === 0 && !busy;
  const showPassword = requestPassword && allGroups.length === 1 && incompleteGroups.length === 0;
  const preparedFolderName = validGroups.length === 1
    ? (validGroups[0].extractionFolderName || validGroups[0].exactFileNames?.[0] || '')
    : '';

  busyRef.current = busy;
  onCancelRef.current = onCancel;

  useEffect(() => {
    const priorFocus = document.activeElement;
    cancelRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !busyRef.current) {
        event.preventDefault();
        onCancelRef.current?.();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )];
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (priorFocus instanceof HTMLElement) priorFocus.focus();
    };
  }, []);

  if (allGroups.length === 0) return null;

  const sharedPayload = {
    archiveFiles,
    archiveGroups: allGroups,
    destinationPath: detection?.destinationPath || '',
    unrelatedFiles: detection?.unrelatedFiles || [],
  };
  const hasMissingParts = incompleteGroups.some((group) => group.missingPartNumbers?.length > 0);
  const hasDuplicateParts = incompleteGroups.some((group) => group.duplicatePartNumbers?.length > 0);

  const handlePrepare = () => {
    if (!canPrepare) return;
    onPrepare?.({
      ...sharedPayload,
      mode: 'prepare',
      password,
      preserveExactFileNames: true,
    });
  };

  const handleKeepArchive = () => {
    if (busy || hasDuplicateParts) return;
    onKeepArchive?.({
      ...sharedPayload,
      mode: 'archive-only',
      preserveExactFileNames: true,
    });
  };

  return (
    <div
      className="split-archive-choice-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel?.();
      }}
      role="presentation"
    >
      <form
        aria-busy={busy}
        aria-describedby="split-archive-choice-description"
        aria-labelledby="split-archive-choice-title"
        aria-modal="true"
        autoComplete="off"
        className="split-archive-choice-dialog"
        data-form-type="other"
        name="split-archive-preparation-options"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          handlePrepare();
        }}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <button
          aria-label="Close"
          className="split-archive-choice-close"
          disabled={busy}
          onClick={onCancel}
          type="button"
        >
          <X size={18} />
        </button>

        <div className="split-archive-choice-heading">
          <span className="split-archive-choice-icon"><Files aria-hidden="true" size={22} /></span>
          <div>
            <h2 id="split-archive-choice-title">Organize this ZIP for review?</h2>
            <p id="split-archive-choice-description">
              {incompleteGroups.length > 0
                ? 'CaseDB needs all of the numbered ZIP files before it can open them. You can still upload the files you selected.'
                : 'Upload the selected ZIP files as they are, or have CaseDB open the ZIP and organize the documents inside.'}
            </p>
          </div>
        </div>

        <div className="split-archive-choice-groups">
          {allGroups.map((group) => (
            <section className="split-archive-choice-group" key={group.id}>
              <div className="split-archive-choice-group-header">
                <strong>{group.basename}</strong>
                <span>{group.files.length} file{group.files.length === 1 ? '' : 's'} selected</span>
              </div>
              <ul aria-label={`${group.basename} selected files`}>
                {group.exactFileNames.map((fileName, index) => (
                  <li key={`${fileName}-${index}`}>
                    <Lock aria-hidden="true" size={13} />
                    <span>{fileName}</span>
                  </li>
                ))}
              </ul>

              {group.missingPartNumbers?.length > 0 && (
                <p className="split-archive-choice-warning" role="status">
                  Select {getMissingFileNames(group).join(', ')} too before CaseDB can open this ZIP.
                </p>
              )}
              {group.duplicatePartNumbers?.length > 0 && (
                <p className="split-archive-choice-warning" role="status">
                  Remove the extra file{group.duplicatePartNumbers.length === 1 ? '' : 's'} ending in{' '}
                  {group.duplicatePartNumbers.map((part) => `.${formatPartNumber(part)}`).join(', ')} before continuing.
                </p>
              )}
            </section>
          ))}
        </div>

        <p className="split-archive-choice-name-note">
          The selected ZIP files stay in the folder you chose.{' '}
          {incompleteGroups.length === 0 && (
            preparedFolderName
              ? `If you also organize them, the documents inside go in a new “${preparedFolderName}” folder beside them.`
              : 'If you also organize them, the documents inside go in a new folder named after the first numbered file.'
          )}
        </p>

        {unrelatedFileCount > 0 && (
          <p className="split-archive-choice-name-note">
            {unrelatedFileCount} other selected file{unrelatedFileCount === 1 ? '' : 's'} will continue through the normal upload.
          </p>
        )}

        {showPassword && (
          <div className="split-archive-choice-password-options">
            <Checkbox
              ariaLabel="This ZIP needs a password"
              disabled={busy}
              label="This ZIP needs a password"
              onChange={() => {
                const nextNeedsPassword = !needsPassword;
                setNeedsPassword(nextNeedsPassword);
                if (!nextNeedsPassword) setPassword('');
              }}
              value={needsPassword}
            />
            {needsPassword && (
              <label className="split-archive-choice-password">
                <span>ZIP password</span>
                <input
                  autoCapitalize="none"
                  autoComplete="new-password"
                  autoCorrect="off"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                  data-lpignore="true"
                  disabled={busy}
                  name="archive_decryption_key"
                  onChange={(event) => setPassword(event.target.value)}
                  spellCheck="false"
                  type="password"
                  value={password}
                />
              </label>
            )}
          </div>
        )}

        {(hasMissingParts || hasDuplicateParts) && (
          <p className="split-archive-choice-help">
            {hasDuplicateParts
              ? 'Remove the duplicate numbered files before uploading this package.'
              : 'You can cancel and select all numbered files together, or upload the selected files without opening them.'}
          </p>
        )}

        {allGroups.length > 1 && incompleteGroups.length === 0 && (
          <p className="split-archive-choice-help">
            Select one ZIP at a time to organize it. You can still upload all the selected files now.
          </p>
        )}

        {error && <p className="split-archive-choice-error" role="alert">{error}</p>}

        <div className="split-archive-choice-actions">
          <button className="action alt" disabled={busy} onClick={onCancel} ref={cancelRef} type="button">
            Cancel
          </button>
          <button className="action alt" disabled={busy || hasDuplicateParts} onClick={handleKeepArchive} type="button">
            Upload ZIP only
          </button>
          <button className="action" disabled={!canPrepare} type="submit">
            {busy ? 'Starting…' : 'Upload and organize'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SplitArchiveChoiceModal;
